import React, { useState, useEffect } from "react";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import { getMappedFilters, getSessionFilters } from "../../utils/FilterHelpers";
import { getObject, setObject } from "../../utils/StorageHelpers";
import MissingData from "../../components/Shared/MissingData";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import BaseActionButton from "../../components/Buttons/BaseActionButton";
import DeleteButton from "../../components/Buttons/DeleteButton";
import MainLoadingButton from "../../components/Buttons/MainLoadingButton";
import DropdownButton from "../../components/Buttons/DropdownButton";
import ShareModal from "../../components/Modals/ShareModal";
import PromotionCodesAPI from "../../API/promotionCodes.api";
import { longDateTime } from "../../utils/DateHelper";
import { SuccessAlert } from "../../utils/Alerts";
import { PROMO_CODE_STATUS } from "../../utils/DiscountConstants";
import { useHistory, useLocation } from "react-router-dom";
import BaseAlertModal from "../../components/Modals/BaseAlertModal";
import PromoCodesExportModal from "../../components/Modals/PromoCodesExportModal";
import { Trans, useTranslation } from "react-i18next";
import AddPromoCodeModal from "../../components/Modals/AddPromoCodeModal";
import BreadCrumb from "../../components/Navbars/BreadCrumb";

const PromotionCodes = () => {
    //Data States
    const [promoCodes, setPromoCodes] = useState([]);
    const [promoCodesTotalCount, setPromoCodesDataCount] = useState(0);
    const [selectedPromoCodes, setSelectedPromoCodes] = useState([]);
    const [rowSelectedPromoCode, setRowSelectedPromoCode] = useState({});
    const [isCheckboxIntermediate, setIsCheckboxIntermediate] = useState(false);
    const [isAllSelected, setIsAllSelected] = useState(false);

    //Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Promo-Codes-itemsPerPage", true) || 10
    );

    //Filter States
    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("promoCodes-filters")
    );
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Modal States
    const [deleteModal, setDeleteModal] = useState(false);
    const [shareModal, setShareModal] = useState(false);
    const [exportModal, setExportModal] = useState(false);
    const [addPromoModal, setAddPromoModal] = useState(false);

    //General States
    const [isLoading, setIsLoading] = useState(false);
    const [isSingleAction, setIsSingleAction] = useState(false);

    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const couponId = location.state?.couponId;
    const couponName = location.state?.couponName;

    const headers = [
        { label: t("code"), name: "code" },
        { label: t("status"), name: "status" },
        { label: t("expirationDate"), name: "expiresAt", skipSort: true },
        { label: t("redemptionDate"), name: "redeemedAt" },
        { label: t("redeemedBy"), name: "redeemedBy" },
        { label: t("actions"), skipSort: true },
    ];

    const keys = [
        {
            name: "code",
            element: (row, index) => (
                <div className="flex">
                    <div className="checkbox-wrapper-input filter-checker-btn modal-input text-left">
                        <input
                            onChange={() =>
                                setSelectedPromoCodes((prevSelected) => {
                                    const newSelected = [...prevSelected];
                                    newSelected[index] = !newSelected[index];
                                    return newSelected;
                                })
                            }
                            type="checkbox"
                            className="checkbox-element "
                            id={`checkbox-option-${index}`}
                            data-filter={`filter-content-${index}`}
                            checked={selectedPromoCodes[index]}
                        />
                        <label
                            className="mb-0"
                            htmlFor={`checkbox-option-${index}`}
                        ></label>
                    </div>
                    <div>{row.code}</div>
                </div>
            ),
        },
        { name: "status", element: (row) => PROMO_CODE_STATUS[row.status] },
        {
            name: "expiresAt",
            element: (row) =>
                row.expiresAt ? longDateTime(row.expiresAt) : "---",
        },
        {
            name: "redeemedAt",
            element: (row) =>
                row.redeemedAt ? longDateTime(row.redeemedAt) : "---",
        },
        { name: "redeemedBy" },
        {
            name: "actions",
            element: (row) => (
                <>
                    <div className="flex">
                        <BaseActionButton
                            className="table-btn table-btn-blue mr-2"
                            icon="fas fa-copy"
                            onClick={() => handleCopyToClipboard(row.code)}
                            tippy={true}
                            tippySettings={{ content: t("copy") }}
                        />
                        <BaseActionButton
                            className="table-btn table-btn-blue mr-2"
                            icon="fas fa-share-alt"
                            onClick={() => {
                                setRowSelectedPromoCode(row);
                                setShareModal(true);
                            }}
                            tippy={true}
                            tippySettings={{ content: t("shareViaEmail") }}
                        />
                        <DeleteButton
                            onClick={() => {
                                setDeleteModal(true);
                                setIsSingleAction(true);
                                setRowSelectedPromoCode(row);
                            }}
                            tippy={true}
                        />
                    </div>
                </>
            ),
        },
    ];

    const searchFilters = [
        {
            label: t("redeemedBy"),
            name: "redeemedBy",
            type: "Input",
            placeholder: t("inputPlaceholder", { entity: t("redeemedBy") }),
        },
        {
            label: t("status"),
            name: "status",
            type: "Dropdown",
            options: Object.entries(PROMO_CODE_STATUS).map(
                ([typeNumber, typeLabel]) => ({
                    value: typeNumber,
                    label: typeLabel,
                })
            ),
        },
        {
            label: t("expirationDate"),
            name: "expiresAt",
            type: "Date",
        },
        {
            label: t("redemptionDate"),
            name: "redeemedAt",
            type: "Date",
        },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/coupons",
            element: () => <Trans i18nKey="coupons">Coupons</Trans>,
        },
        {
            element: () => <Trans i18nKey="promotionCode">promotionCode</Trans>,
        },
    ];

    useEffect(() => {
        if (!couponId) {
            history.push("/page-not-found");
        }
    }, []);

    useEffect(() => {
        const selectedPromoCodeCount = selectedPromoCodes.filter(
            (isSelected) => isSelected
        ).length;
        setIsAllSelected(selectedPromoCodeCount === promoCodes.length);
        setIsCheckboxIntermediate(
            selectedPromoCodeCount > 0 &&
                selectedPromoCodeCount < promoCodes.length
        );
    }, [selectedPromoCodes]);

    useEffect(() => {
        if (sortBy && sortDirection) {
            setCurrentPage(1);
            listPromoCodes(appliedFilters, sortBy, sortDirection);
        } else {
            setCurrentPage(1);
            listPromoCodes(appliedFilters);
        }
    }, [sortBy, sortDirection]);

    useEffect(() => {
        setSelectedPromoCodes(
            promoCodes?.length ? Array(promoCodes.length).fill(false) : []
        );
    }, [promoCodes]);

    const listPromoCodes = async (
        filters = null,
        defaultSortBy = null,
        defaultSortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest || 10
    ) => {
        setIsLoading(true);

        const result = await PromotionCodesAPI.listPromotionCodes(
            itemsPerPage,
            getMappedFilters(filters, [], ["status"]),
            offset,
            defaultSortBy,
            defaultSortDirection,
            couponId
        );

        setPromoCodes(result?.data);
        setPromoCodesDataCount(result?.totalCount);

        setIsLoading(false);
    };

    const increasePromos = async (payload) => {
        const result = await PromotionCodesAPI.createPromotionCodes(payload);
        if (result?.inserted) {
            SuccessAlert(
                t("addSuccessMessage", { entity: t("promotionCodes") })
            );
            setAddPromoModal(false);
            listPromoCodes(appliedFilters, sortBy, sortDirection);
            setCurrentPage(1);
        }
    };

    const handleDeletePromoCode = async () => {
        const result = await PromotionCodesAPI.deletePromotionCodes(
            rowSelectedPromoCode.id
        );

        if (result?.deleted) {
            SuccessAlert(t("deleteSuccessMessage", { entity: t("codes") }));
            setDeleteModal(false);
            setRowSelectedPromoCode({});
            setIsSingleAction(false);

            let newCurrentPage = currentPage;
            if (promoCodes.lenth <= 1 && currentPage > 1) {
                newCurrentPage = currentPage - 1;
            }
            listPromoCodes(filters, sortBy, sortDirection, newCurrentPage - 1);
            setCurrentPage(newCurrentPage);
        }
    };

    const handleDeleteBulkPromoCodes = async () => {
        const result = await PromotionCodesAPI.deleteBulkPromotionCodes(
            getSelectedPromoCodes()
        );

        if (result?.deleted) {
            SuccessAlert(t("deleteSuccessMessage", { entity: t("codes") }));
            setDeleteModal(false);
            setRowSelectedPromoCode({});
            let newCurrentPage = currentPage;
            if (promoCodes.length <= 1 && currentPage > 1) {
                newCurrentPage = currentPage - 1;
            }
            listPromoCodes(filters, sortBy, sortDirection, newCurrentPage - 1);
            setCurrentPage(newCurrentPage);
        }
    };

    const applyFilters = (specificAppliedFilters) => {
        listPromoCodes(specificAppliedFilters, sortBy, sortDirection);
        setCurrentPage(1);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            listPromoCodes(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Coupons-itemsPerPage", itemsPerPage, true);
        listPromoCodes(filters, sortBy, sortDirection, 0, itemsPerPage);
        setCurrentPage(1);
    };

    const getSelectedCount = () => {
        return selectedPromoCodes.filter((selected) => selected).length;
    };

    const handleCopyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        SuccessAlert(t("copiedMessage", { entity: t("codes") }));
    };

    const getSelectedPromoCodes = (key = "id") => {
        const codes = [];
        promoCodes.forEach((promoCode, index) => {
            if (selectedPromoCodes[index]) {
                codes.push(promoCode[key]);
            }
        });
        return codes;
    };

    const handleCopyBulkToClipboad = () => {
        const codes = getSelectedPromoCodes("code");
        handleCopyToClipboard(codes.join("\n"));
    };

    const getDropdownButtonOptions = (text, iconClassName) => {
        return (
            <div className="flex items-center">
                <i className={`${iconClassName} mr-2`}></i>
                {text}
            </div>
        );
    };

    const dropdownButtonOptions = [
        {
            label: getDropdownButtonOptions(t("delete"), "fas fa-trash"),
            callback: () => setDeleteModal(true),
        },
        {
            label: getDropdownButtonOptions(t("copy"), "fas fa-copy"),
            callback: handleCopyBulkToClipboad,
        },
    ];

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-content-header flex items-center justify-between">
                <div className="flex flex-col">
                    <h1 className="h1">{t("promotionCodes")}</h1>
                    <span className="text-primary-color-blue">
                        {`${t("for")} ${couponName}`}
                    </span>
                </div>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={searchFilters}
                        filters={filters}
                        setFilters={setFilters}
                        applyFilters={applyFilters}
                        component="promoCodes"
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                    />
                    <MainLoadingButton
                        buttonText={t("addTitle", {
                            entity: t("promotionCodes"),
                        })}
                        className="default-orange-outline-btn ml-1"
                        iconClassName="fas fa-plus"
                        onClick={() => setAddPromoModal(true)}
                    />
                    <MainLoadingButton
                        buttonText={t("export")}
                        className="default-orange-outline-btn ml-1"
                        iconClassName="fas fa-file-download"
                        onClick={() => setExportModal(true)}
                    />
                    <DropdownButton
                        icon="fas fa-cogs"
                        options={dropdownButtonOptions}
                        disabled={getSelectedCount() === 0}
                        buttonName={`${t("actions")} ${
                            getSelectedCount() ? `(${getSelectedCount()})` : ""
                        }`}
                        className="ml-1"
                    />
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {promoCodesTotalCount > 0 ? (
                        <>
                            <CardTable
                                keys={keys}
                                headers={headers}
                                rows={promoCodes}
                                purpose={"promoCodes"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                setSelectedRows={setSelectedPromoCodes}
                                leftAlignFirstColumn={true}
                                isAllRowsSelected={isAllSelected}
                                setIsAllRowsSelected={setIsAllSelected}
                                isCheckboxIntermediate={isCheckboxIntermediate}
                                firstChildClassName="justify-start"
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={promoCodesTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData isFilterUsed={true} />
                    )}
                </div>
            </LoadingOverlay>
            <ShareModal
                isVisible={shareModal}
                setIsVisible={setShareModal}
                selectedRow={rowSelectedPromoCode}
                setSelectedRow={setRowSelectedPromoCode}
            />
            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={t("deleteTitle", { purpose: t("codes") })}
                message={t("deleteMessage", { purpose: t("codes") })}
                action={{
                    submitAction: isSingleAction
                        ? handleDeletePromoCode
                        : handleDeleteBulkPromoCodes,
                }}
                type="delete"
            />
            <AddPromoCodeModal
                isVisible={addPromoModal}
                setIsVisible={setAddPromoModal}
                couponId={couponId}
                addAction={increasePromos}
            />

            <PromoCodesExportModal
                isVisible={exportModal}
                setIsVisible={setExportModal}
                couponId={couponId}
            />
        </>
    );
};

export default PromotionCodes;
